import { useState, useEffect, useRef } from "react";
import { Dropdown, Image as AntImage, MenuProps, Spin, Tag, message } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { CommonButton } from "@components/CommonButton";
import Image from "@components/Image";
import libraryService from "@apis/libraryService";
import { IconDelete, IconClose, IconMore } from "@icons/index";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { fetchVideoGenerateList, hideTrashDisplay, statusConfig } from "@pages/library/librarySlice";
import { LIBRARY_IMAGE_ASSETS_REFRESH_EVENT } from "@pages/library/events";
import { useKeyup } from "@hooks/utils";
import VideoCover from "@layouts/VideoCover";
import NoResult from "@components/Section/layouts/NoResult";
import { TrashImageCard } from "./TrashImageCard";

// 回收站卡片组件
const TrashCard = ({
  record,
  onRestore,
  onDelete,
}: {
  record: any;
  onRestore: (id: string | number) => void;
  onDelete: (id: string | number) => void;
}) => {
  const status = record?.jobItem?.status;
  const context = record?.jobItem?.context || {};
  const config = (statusConfig as any)[status || "queued"];

  const menuItems: MenuProps["items"] = [
    {
      key: "restore",
      label: (
        <div
          className="w-full h-full flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            onRestore(record.id);
          }}
        >
          <Trans>Restore</Trans>
        </div>
      ),
    },
    {
      key: "delete",
      label: (
        <div
          className="w-full h-full flex items-center text-kds-danger-fg"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(record.id);
          }}
        >
          <Trans>Delete forever</Trans>
        </div>
      ),
    },
  ];

  // 渲染封面区域
  const renderCover = () => {
    return (
      <div className="relative w-full aspect-3/4">
        <VideoCover
          cover={context.coverImageUrl}
          className="w-full aspect-3/4 rounded-0! border-none! rounded-none!"
          videoUrl={context?.videoUrl}
          aspectRatio={context?.aspectRatio}
          showAdIcon={false}
          videoIconSize="size48"
          adIconSize="size24"
          videoType="video"
        ></VideoCover>
      </div>
    );
  };

  return (
    <div className="kalo-white-card flex flex-col w-full group relative p-0! overflow-hidden cursor-pointer">
      <div className="relative w-full aspect-3/4">{renderCover()}</div>

      <div className="p-[16px]">
        {/* 商品信息 */}
        <div className="pb-[12px] flex items-start gap-[8px] border-b border-kds-border">
          {context.productCoverImageUrl ? (
            <Image className="w-[32px] h-[32px] shrink-0 rounded-[4px]" url={context.productCoverImageUrl} />
          ) : (
            <div className="w-[32px] h-[32px] shrink-0 rounded-[4px] bg-kds-bg-soft border border-kds-border" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-kds-fg leading-[18px] line-clamp-2 m-0">{context.productTitle || "-"}</p>
          </div>
        </div>

        {/* 状态栏 */}
        <div className="flex items-center justify-between pt-[12px]">
          <Tag color={config.color} className="rounded-full!">
            <Trans>{config.label}</Trans>
          </Tag>
        </div>
      </div>

      {/* 右上角恢复菜单 */}
      <Dropdown trigger={["hover"]} menu={{ items: menuItems }} placement="bottomLeft">
        <div
          className="absolute right-[11px] top-[11px] w-[24px] h-[24px] gap-[3px] cursor-pointer hidden group-hover:flex"
          onClick={(e) => e.stopPropagation()}
        >
          <IconMore className="w-[24px] h-[24px]" />
        </div>
      </Dropdown>
    </div>
  );
};

const GlobalTrashModal = () => {
  const dispatch = useAppDispatch();
  const { trashDisplay } = useAppSelector((state) => state.libraryReducer);
  const [trashList, setTrashList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [emptyLoading, setEmptyLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const previewOpenRef = useRef(false);
  const ignoreNextEscapeCloseRef = useRef(false);

  // 控制 body 滚动
  if (trashDisplay) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "overlay";
  }

  const handleClose = () => {
    dispatch(hideTrashDisplay());
    dispatch(fetchVideoGenerateList({}));
  };

  const handlePreview = (src: string) => {
    if (!src) return;
    setPreviewSrc(src);
    setPreviewOpen(true);
    previewOpenRef.current = true;
  };

  const handlePreviewVisibleChange = (visible: boolean) => {
    setPreviewOpen(visible);
    previewOpenRef.current = visible;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && previewOpenRef.current) {
        ignoreNextEscapeCloseRef.current = true;
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  // ESC 键关闭：预览打开时让 AntImage 优先消化 ESC
  useKeyup(() => {
    if (previewOpenRef.current || ignoreNextEscapeCloseRef.current) {
      ignoreNextEscapeCloseRef.current = false;
      return;
    }
    handleClose();
  });

  const fetchTrashList = async () => {
    setLoading(true);
    try {
      const result = await libraryService.fetchTrashList<any[]>();
      setTrashList(result || []);
    } catch (error) {
      console.error("Failed to fetch trash list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trashDisplay) {
      fetchTrashList();
    }
  }, [trashDisplay]);

  const handleEmptyTrash = async () => {
    setEmptyLoading(true);
    try {
      await libraryService.emptyTrash();
      setTrashList([]);
    } catch (error) {
      console.error("Failed to empty trash:", error);
    } finally {
      setEmptyLoading(false);
    }
  };

  const handleRestore = async (rawId: string | number, assetType?: string) => {
    const id = String(rawId);
    try {
      await libraryService.restoreFromTrash(id);
      setTrashList((prev) => prev.filter((item) => String(item.id) !== id));
      if (assetType === "IMG") {
        window.dispatchEvent(new CustomEvent(LIBRARY_IMAGE_ASSETS_REFRESH_EVENT));
      }
    } catch (error) {
      console.error("Failed to restore item:", error);
    }
  };

  const handleDelete = async (rawId: string | number) => {
    const id = String(rawId);
    try {
      await libraryService.deleteFromTrash(id);
      setTrashList((prev) => prev.filter((item) => String(item.id) !== id));
    } catch (error) {
      console.error("Failed to delete item:", error);
      const backendMessage =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as { message?: string })?.message;
      message.error(backendMessage ? t(backendMessage) : t("Failed"));
    }
  };

  if (trashDisplay) {
    return createPortal(
      <div className="fixed inset-0 z-1000 bg-kds-bg-soft">
        <div className="absolute inset-0 bg-kds-bg-layout overflow-hidden">
          {/* 标题栏 */}
          <div className="flex items-center gap-[16px] px-[32px] py-[12px] border-b border-kds-border">
            <CommonButton className="w-[32px] h-[32px] p-0!" onClick={handleClose}>
              <IconClose className="w-[24px]! h-[24px]!" />
            </CommonButton>
            <span className="text-kds-fg font-semibold text-[16px]">
              <Trans>Trash</Trans>
            </span>
          </div>

          <div className="px-[32px] py-[16px]">
            {/* 提示和操作栏 */}
            <div className="flex justify-between items-center mb-[12px]">
              <span className="text-kds-fg-subtle text-[14px] font-normal">
                <Trans
                  components={{ span: <span className="text-kds-fg" /> }}
                  defaults="Items are permanently deleted <span>30 days</span> after being moved to the Trash."
                />
              </span>
              <CommonButton onClick={handleEmptyTrash} loading={emptyLoading} disabled={trashList.length === 0}>
                <IconDelete className="w-[16px] h-[16px] [&_path]:stroke-current" />
                <Trans>Empty Trash</Trans>
              </CommonButton>
            </div>

            {/* 内容区域 */}
            <Spin spinning={loading}>
              <div className="h-[calc(100vh-150px)] overflow-y-auto scrollbar-none">
                {trashList.length > 0 ? (
                  <div className="grid grid-cols-5 min-[1920px]:grid-cols-6 gap-[16px]">
                    {trashList.map((item) =>
                      item?.assetType === "IMG" ? (
                        <TrashImageCard
                          key={item.id}
                          record={item}
                          onPreview={handlePreview}
                          onRestore={(id) => handleRestore(id, item.assetType)}
                          onDelete={handleDelete}
                        />
                      ) : (
                        <TrashCard
                          key={item.id}
                          record={item}
                          onRestore={(id) => handleRestore(id, item.assetType)}
                          onDelete={handleDelete}
                        />
                      ),
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[400px]">
                    <NoResult blankTip="Trash is empty" />
                  </div>
                )}
              </div>
            </Spin>
          </div>
        </div>

        <AntImage
          src={previewSrc}
          styles={{ root: { display: "none" } }}
          preview={{
            visible: previewOpen,
            onVisibleChange: handlePreviewVisibleChange,
          }}
        />
      </div>,
      document.body,
    );
  }

  return null;
};

export default GlobalTrashModal;
